import type { Vendor } from './types';
import type { VendorRow } from './schema';
import { resolveVendorImageUrl } from '../../utils/vendorImageUrl';

function parseStatus(raw: string | undefined): Vendor['status'] {
  const s = (raw ?? 'active').toLowerCase().trim();
  // Anything other than 'active' (or blank) is treated as inactive — drop
  // it from all surfaces. Includes legacy values like 'pending' / 'disabled'.
  return s === 'active' || s === '' ? 'active' : 'inactive';
}

/**
 * Converts a Zod-validated VendorRow (from the API or demo data) into a Vendor.
 * Uses conditional spreads so optional fields are absent (not undefined) when
 * not provided, satisfying exactOptionalPropertyTypes.
 */
export function vendorRowToVendor(
  r: VendorRow,
  coordinates: [number, number],
  // Amenity-typed positions get a synthetic vendor (utils/amenities) and
  // never reach this builder, so the only booth type is 'vendor'.
  type: 'vendor',
  labelPosition: 'left' | 'right' | 'top' | 'bottom',
  positionUid: string,
): Vendor {
  const image = r.image ? resolveVendorImageUrl(r.image) : undefined;

  return {
    uid: r.uid,
    name: r.name,
    positionId: r.position_id,
    positionUid,
    categoryId: r.category,
    description: r.description,
    ...(r.here !== undefined ? { here: r.here } : {}),
    ...(r.instagram !== undefined ? { instagram: r.instagram } : {}),
    ...(r.facebook !== undefined ? { facebook: r.facebook } : {}),
    ...(r.website !== undefined ? { website: r.website } : {}),
    ...(image !== undefined ? { image } : {}),
    status: parseStatus(r.status),
    coordinates,
    type,
    labelPosition,
  };
}
